import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../../api/axios';
import { getImageUrl } from '../../utils/image';

import config from '../../config';
import LayoutWrapper from '../../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler, EmptyState } from '../../components/common/ErrorHandler';
import PermissionGuard, { usePermission } from '../../components/Guards/PermissionGuard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  UserCircle,
  Download,
  Printer,
  FileSpreadsheet
} from 'lucide-react';
import useToast from '../../hooks/useToast';
import { generateMembersReport, generateMembershipForm, generateGuestReceipt } from '../../components/prints/clubPrintTemplates';

const MembersList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  const [members, setMembers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('type') || 'ALL');

  const canCreate = usePermission('membership', 'create');
  const canEdit = usePermission('membership', 'edit');
  const canDelete = usePermission('membership', 'delete');

  useEffect(() => {
    fetchMembers();
  }, [activeTab]);

  const fetchMembers = async () => {
    try {
      setLoading(true);

      if (activeTab === 'Guest') {
        const response = await api.get('/api/guests');
        setGuests(response.data);
        setMembers([]);
      } else {
        const params = {};
        if (activeTab !== 'ALL') {
          params.membership_type = activeTab;
        }
        const response = await api.get('/api/members', { params });
        setMembers(response.data);

        if (activeTab === 'ALL') {
          const guestsResponse = await api.get('/api/guests');
          setGuests(guestsResponse.data);
        } else {
          setGuests([]);
        }
      }

      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (item, isGuest = false) => {
    try {
      if (isGuest) {
        const doc = generateGuestReceipt(item);
        doc.save(`guest_receipt_${item.membership_no}.pdf`);
      } else {
        const doc = generateMembershipForm(item);
        doc.save(`membership_form_${item.membership_no}.pdf`);
      }
      showSuccess('PDF generated successfully');
    } catch (err) {
      showError('Failed to generate PDF');
      console.error('PDF generation error:', err);
    }
  };

  const handleDelete = async (id, isGuest = false) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;

    try {
      const endpoint = isGuest ? `/api/guests/${id}` : `/api/members/${id}`;
      await api.delete(endpoint);
      showSuccess('Member deleted successfully');
      fetchMembers();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete member');
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams(value === 'ALL' ? {} : { type: value });
  };

  const handleExportPDF = () => {
    try {
      const allData = [...members, ...guests.map(g => ({ ...g, membership_type: 'Guest' }))];
      const doc = generateMembersReport(allData, `${activeTab} Members Report`);
      doc.save(`members_report_${activeTab.toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`);
      showSuccess('PDF exported successfully');
    } catch (err) {
      showError('Failed to export PDF');
      console.error('PDF export error:', err);
    }
  };

  const handleExportExcel = () => {
    try {
      const allData = [...members, ...guests];
      
      const excelData = allData.map((item, index) => ({
        'S.No': index + 1,
        'Member No': item.membership_no || '',
        'Type': item.membership_type || (item.guest_name ? 'Guest' : ''),
        'Name': item.applicant_name || item.guest_name || '',
        'Father Name': item.father_name || '-',
        'Mobile': item.mobile || '-',
        'Email': item.email || '-',
        'DOB': item.dob || '-',
        'Sex': item.sex || '-',
        'Marital Status': item.marital_status || '-',
        'Address': item.address || '-',
        'Qualification': item.qualification || '-',
        'Organization': item.organization || '-',
        'Designation': item.designation || '-',
        'Aadhaar': item.aadhaar || '-',
        'PAN': item.pan || '-',
        'Membership Fee': item.membership_fee || item.amount || 0,
        'Opening Balance': item.opening_balance || 0,
        'Subscription From': item.subscription_from || '-',
        'Subscription To': item.subscription_to || '-',
        'Status': item.status || '',
        'Date': item.date || '',
        'Remarks': item.remarks || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Members');
      
      // Auto-size columns
      const colWidths = [];
      const headers = Object.keys(excelData[0] || {});
      headers.forEach((header, idx) => {
        const maxLength = Math.max(
          header.length,
          ...excelData.map(row => String(row[header] || '').length)
        );
        colWidths[idx] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      XLSX.writeFile(wb, `members_${activeTab.toLowerCase()}_${new Date().toISOString().split('T')[0]}.xlsx`);
      showSuccess('Excel exported successfully');
    } catch (err) {
      showError('Failed to export Excel');
      console.error('Excel export error:', err);
    }
  };

  const filteredMembers = members.filter(m =>
    !search ||
    m.membership_no?.toLowerCase().includes(search.toLowerCase()) ||
    m.applicant_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.mobile?.includes(search) ||
    m.email?.toLowerCase().includes(search.toLowerCase()) ||
    m.aadhaar?.includes(search) ||
    m.pan?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredGuests = guests.filter(g =>
    !search ||
    g.membership_no?.toLowerCase().includes(search.toLowerCase()) ||
    g.guest_name?.toLowerCase().includes(search.toLowerCase()) ||
    g.member_reference_name?.toLowerCase().includes(search.toLowerCase())
  );

  const calculateDaysLeft = (toDate) => {
    if (!toDate) return null;
    const end = new Date(toDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <LayoutWrapper title="Membership">
        <LoadingSpinner text="Loading members..." />
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper
      title="Membership Management"
      subtitle="Manage permanent, temporary, and guest members"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportExcel} 
            className="border-green-600 text-green-600 hover:bg-green-50 text-sm" 
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Export Excel</span>
            <span className="sm:hidden">Excel</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportPDF} 
            className="border-blue-600 text-blue-600 hover:bg-blue-50 text-sm" 
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Export PDF</span>
            <span className="sm:hidden">PDF</span>
          </Button>
          <PermissionGuard module="membership" action="create">
            <Button 
              onClick={() => navigate('/members/add')} 
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm" 
              data-testid="add-member-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Add Member</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </PermissionGuard>
        </div>
      }
    >
      {error && <ErrorHandler error={error} onRetry={fetchMembers} />}

      {/* Tabs & Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
              <TabsList className="grid grid-cols-4 w-full sm:w-auto bg-gray-100">
               <TabsTrigger
  value="ALL"
  data-testid="tab-all"
  className="
    text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  All
</TabsTrigger>

<TabsTrigger
  value="Permanent"
  data-testid="tab-permanent"
  className="
    text-xs sm:text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  <span className="hidden sm:inline">Permanent</span>
  <span className="sm:hidden">Perm</span>
</TabsTrigger>

<TabsTrigger
  value="Temporary"
  data-testid="tab-temporary"
  className="
    text-xs sm:text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  <span className="hidden sm:inline">Temporary</span>
  <span className="sm:hidden">Temp</span>
</TabsTrigger>

<TabsTrigger
  value="Guest"
  data-testid="tab-guest"
  className="
    text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  Guest
</TabsTrigger>

              </TabsList>
            </Tabs>
<div className="relative w-full sm:w-80">
  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />

  <Input
    placeholder="Search by name, ID, mobile..."
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    data-testid="search-input"
    className="h-11 !pl-12 pr-3 py-0 text-sm"
  />
</div>



          </div>
        </div>

        {/* Members Table - with hidden scrollbar */}
        {activeTab !== 'Guest' && filteredMembers.length > 0 && (
          <div 
            className="overflow-x-auto scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <style jsx>{`
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="w-16 font-semibold text-gray-900 text-sm whitespace-nowrap">#</TableHead>
                  {activeTab === 'ALL' && <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Type</TableHead>}
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Photo</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Member No</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Name</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Mobile</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Email</TableHead>
                  {activeTab === 'Permanent' && <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Address</TableHead>}
                  {activeTab === 'Temporary' && (
                    <>
                      <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">From Date</TableHead>
                      <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">To Date</TableHead>
                      <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Days Left</TableHead>
                    </>
                  )}
                  {(activeTab === 'ALL' || activeTab === 'Permanent') && <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Joined</TableHead>}
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Status</TableHead>
                  <TableHead className="text-right font-semibold text-gray-900 text-sm whitespace-nowrap">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member, index) => {
                  const daysLeft = member.membership_type === 'Temporary'
                    ? calculateDaysLeft(member.subscription_to)
                    : null;

                  return (
                    <TableRow key={member.id} className="hover:bg-gray-50" data-testid={`member-row-${index}`}>
                      <TableCell className="text-gray-700 text-sm font-medium">{index + 1}</TableCell>
                      {activeTab === 'ALL' && (
                        <TableCell>
                          <Badge variant={member.membership_type === 'Permanent' ? 'default' : 'secondary'} className="bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs font-medium whitespace-nowrap">
                            {member.membership_type}
                          </Badge>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center justify-center">
                          {member.profile_picture ? (
                            <img
                              src={`${getImageUrl(member.profile_picture)}?v=${member.updated_at || Date.now()}`}
                              alt={member.applicant_name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center"
                            style={{ display: member.profile_picture ? 'none' : 'flex' }}
                          >
                            <UserCircle className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-gray-900 text-sm whitespace-nowrap">{member.membership_no}</TableCell>
                      <TableCell className="text-gray-800 text-sm font-medium whitespace-nowrap">{member.applicant_name}</TableCell>
                      <TableCell className="text-gray-700 text-sm whitespace-nowrap">{member.mobile}</TableCell>
                      <TableCell className="text-gray-700 text-sm whitespace-nowrap">{member.email || '-'}</TableCell>
                      {activeTab === 'Permanent' && (
                        <TableCell className="text-gray-700 text-sm max-w-[200px] truncate">{member.address || '-'}</TableCell>
                      )}
                      {activeTab === 'Temporary' && (
                        <>
                          <TableCell className="text-gray-700 text-sm whitespace-nowrap">{member.subscription_from || '-'}</TableCell>
                          <TableCell className="text-gray-700 text-sm whitespace-nowrap">{member.subscription_to || '-'}</TableCell>
                          <TableCell>
                            {daysLeft !== null && (
                              <Badge 
                                variant={daysLeft > 0 ? 'default' : daysLeft === 0 ? 'warning' : 'destructive'} 
                                className={`text-xs font-semibold whitespace-nowrap ${
                                  daysLeft > 0 
                                    ? 'bg-green-100 text-green-800 border border-green-300' 
                                    : daysLeft === 0 
                                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' 
                                    : 'bg-red-100 text-red-800 border border-red-300'
                                }`}
                              >
                                {daysLeft > 0 ? `${daysLeft}d left` : daysLeft === 0 ? 'Expires Today' : 'Expired'}
                              </Badge>
                            )}
                          </TableCell>
                        </>
                      )}
                      {(activeTab === 'ALL' || activeTab === 'Permanent') && (
                        <TableCell className="text-gray-700 text-sm whitespace-nowrap">{member.date || '-'}</TableCell>
                      )}
                      <TableCell>
                        <Badge 
                          variant={member.status === 'Active' ? 'success' : 'secondary'} 
                          className={`text-xs font-semibold whitespace-nowrap ${
                            member.status === 'Active' 
                              ? 'bg-green-100 text-green-800 border border-green-300' 
                              : 'bg-gray-100 text-gray-700 border border-gray-300'
                          }`}
                        >
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="hover:bg-gray-100 h-8 w-8" data-testid={`actions-btn-${index}`}>
                              <MoreVertical className="w-4 h-4 text-gray-600" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
  align="end"
  sideOffset={6}
  className="w-48 bg-white shadow-lg border border-gray-200 z-50"
  collisionPadding={10}
  avoidCollisions
  forceMount
  container={document.body}
>
                            <DropdownMenuItem
                              onClick={() => handlePrint(member)}
                              className="cursor-pointer text-sm"
                            >
                              <Printer className="w-4 h-4 mr-2 text-blue-600" />
                              <span className="text-gray-700">Print</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/members/view/${member.id}`)} className="cursor-pointer text-sm">
                              <Eye className="w-4 h-4 mr-2 text-green-600" />
                              <span className="text-gray-700">View Details</span>
                            </DropdownMenuItem>
                            {canEdit && (
                              <DropdownMenuItem onClick={() => navigate(`/members/edit/${member.id}`)} className="cursor-pointer text-sm">
                                <Edit className="w-4 h-4 mr-2 text-orange-600" />
                                <span className="text-gray-700">Edit</span>
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <DropdownMenuItem
                                onClick={() => handleDelete(member.id)}
                                className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50 text-sm"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Guests Table - with hidden scrollbar */}
        {(activeTab === 'Guest' || activeTab === 'ALL') && filteredGuests.length > 0 && (
          <div 
            className="overflow-x-auto scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <style jsx>{`
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {activeTab === 'ALL' && filteredMembers.length > 0 && (
              <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-b border-blue-200">
                <h4 className="font-semibold text-gray-900 text-sm sm:text-base flex items-center">
                  <UserCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Guest Entries
                </h4>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="w-16 font-semibold text-gray-900 text-sm whitespace-nowrap">#</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Member No</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Guest Name</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Reference</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Amount</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Date</TableHead>
                  <TableHead className="font-semibold text-gray-900 text-sm whitespace-nowrap">Status</TableHead>
                  <TableHead className="text-right font-semibold text-gray-900 text-sm whitespace-nowrap">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGuests.map((guest, index) => (
                  <TableRow key={guest.id} className="hover:bg-gray-50" data-testid={`guest-row-${index}`}>
                    <TableCell className="text-gray-700 text-sm font-medium">{index + 1}</TableCell>
                    <TableCell className="font-semibold text-gray-900 text-sm whitespace-nowrap">{guest.membership_no}</TableCell>
                    <TableCell className="text-gray-800 text-sm font-medium whitespace-nowrap">{guest.guest_name}</TableCell>
                    <TableCell className="text-gray-700 text-sm whitespace-nowrap">{guest.member_reference_name || '-'}</TableCell>
                    <TableCell className="text-gray-700 text-sm font-semibold whitespace-nowrap">₹{(guest.amount || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-gray-700 text-sm whitespace-nowrap">{guest.date}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={guest.status === 'Active' ? 'success' : 'secondary'} 
                        className={`text-xs font-semibold whitespace-nowrap ${
                          guest.status === 'Active' 
                            ? 'bg-green-100 text-green-800 border border-green-300' 
                            : 'bg-gray-100 text-gray-700 border border-gray-300'
                        }`}
                      >
                        {guest.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="hover:bg-gray-100 h-8 w-8" data-testid={`guest-actions-btn-${index}`}>
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
  align="end"
  sideOffset={6}
  className="w-48 bg-white shadow-lg border border-gray-200 z-50"
  collisionPadding={10}
  avoidCollisions
  forceMount
  container={document.body}
>
                          <DropdownMenuItem
                            onClick={() => handlePrint(guest, true)}
                            className="cursor-pointer text-sm"
                          >
                            <Printer className="w-4 h-4 mr-2 text-blue-600" />
                            <span className="text-gray-700">Print</span>
                          </DropdownMenuItem>
                          {canEdit && (
                            <DropdownMenuItem onClick={() => navigate(`/members/edit/${guest.id}?type=guest`)} className="cursor-pointer text-sm">
                              <Edit className="w-4 h-4 mr-2 text-orange-600" />
                              <span className="text-gray-700">Edit</span>
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(guest.id, true)}
                              className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50 text-sm"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Empty State */}
        {filteredMembers.length === 0 && filteredGuests.length === 0 && (
          <EmptyState
            icon={UserCircle}
            title="No members found"
            description={search ? "Try adjusting your search criteria" : "Start by adding your first member"}
            action={
              canCreate && (
                <Button onClick={() => navigate('/members/add')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="empty-add-member-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              )
            }
          />
        )}
      </div>
    </LayoutWrapper>
  );
};

export default MembersList;
